import { useContext } from 'react';
import { gql } from '@apollo/client';

import useSuspenseQuery from 'commons/useSuspenseQuery';
import PromotionContentContext from './PromotionContentContext';

export const GET_PROMOTION_CONTENT_QUERY = gql`
  query getPromotionContent($entryID: String, $carouselEntryID: String) {
    getPromotionContent(entryID: $entryID, carouselEntryID: $carouselEntryID) {
      id
      title
      alternateTitle
      topBannerText
      body
      alternateBody
      disclaimer
      icon
      backgroundImage
      backgroundColor
      heroTextBackgroundColor
      mainTextColor
      heroFancyText
      heroFancyTextColor
      heroLogomark
      endDate
      displayFlashSale

      mediaAssetUltraWide
      mediaAssetExtraWide
      mediaAssetWide
      mediaAssetSquare

      linkUrl

      ctaText
      ctaStyle
      ctaTextColor
      ctaLinkColor
      ctaBackgroundColor

      topBannerTextColor
      topBannerBackgroundColor
      topBannerBackgroundImage

      secondaryBannerText

      saleDropdownLeftSideText
      saleDropdownLinkUrl
      saleDropdownMedia
      saleDropdownAuxPromoText
      saleDropdownAuxPromoUrl

      promotionCarousel {
        id
        slides {
          fancyText
          fancyTextColor
          title
          disclaimer
          mainTextColor
          backgroundImage
          mediaAssetWide
          mediaAssetSquare
          mediaAssetExtraWide
          mediaAssetUltraWide
          ctaText
          ctaStyle
          ctaTextColor
          ctaBackgroundColor
          linkUrl
        }
      }
    }
  }
`;

export default function useContentfulPromotionContent(
  entryID,
  carouselEntryID = ''
) {
  // The root layout fetches the default promotion content on the server and
  // provides it via context (CON-8210). When that value is present and this is
  // a default (non entry-specific) call, return it synchronously and skip the
  // Apollo `useSuspenseQuery` so server and first client render agree on one
  // deterministic value — under the `'use cache'` root layout the Apollo
  // transport is frozen, so the query otherwise diverged and broke hydration.
  // Entry-specific calls (with `entryID`/`carouselEntryID`) keep using Apollo.
  const providedPromotionData = useContext(PromotionContentContext);
  const useProvidedContent =
    providedPromotionData !== undefined &&
    entryID === undefined &&
    carouselEntryID === '';

  const { loading, data } = useSuspenseQuery(GET_PROMOTION_CONTENT_QUERY, {
    variables: { entryID, carouselEntryID },
    skip: useProvidedContent,
  });

  if (useProvidedContent) {
    return {
      loading: false,
      promotionData: providedPromotionData,
    };
  }

  return {
    loading,
    promotionData: (data || {}).getPromotionContent,
  };
}
